// BHP-1083: domain read hooks over Backend2.0 (Hydra). The collection is
// customer-scoped by the backend (Domain reachable via activeSubscriptions), so
// it returns the active customer's domains. Replaces the legacy
// `/api/v1/{customer}/activesubscription` fan-out in DomainService.
import { type UseQueryResult } from '@tanstack/react-query';
import { useCollection, useItem } from '../../../shared/api/useHydra';
import { type HydraCollection } from '../../../shared/api/hydra';
import { type Domain, isRegistrationInFlight } from '../domain';

export function useDomains(page = 1): UseQueryResult<HydraCollection<Domain>> {
  return useCollection<Domain>('domains', { page });
}

/** Poll cadence while an async registration is still in flight. */
export const REGISTRATION_POLL_MS = 5000;

/**
 * Stop polling after ~5 minutes. A registration stuck in `pending` (dead
 * worker, registrar outage) would otherwise keep an interval running for as
 * long as the tab is open — in the background too.
 */
export const REGISTRATION_POLL_LIMIT = 60;

/**
 * Poll while the backend can still change the status, up to the budget.
 * Exported so the rule can be asserted without driving React Query's timers.
 */
export function registrationPollInterval(
  domain: Domain | undefined,
  fetchCount: number
): number | false {
  if (!isRegistrationInFlight(domain?.registrationStatus)) {
    return false;
  }
  return fetchCount < REGISTRATION_POLL_LIMIT ? REGISTRATION_POLL_MS : false;
}

/**
 * Single domain by uuid. While its registration is in flight
 * (pending/registering), the query polls so the detail page reflects the async
 * OpenProvider outcome (registered/failed) without a manual refresh; polling
 * stops once the status is terminal or the poll budget is spent.
 */
export function useDomain(
  uuid: string | null,
  enabled = true
): UseQueryResult<Domain> {
  return useItem<Domain>('domains', uuid ?? '', {
    enabled: enabled && uuid !== null,
    refetchInterval: registrationPollInterval,
    // Keep polling the async registration status even when the tab is not
    // focused (default pauses background intervals).
    refetchIntervalInBackground: true
  });
}
